// ── 연차 대량 붙여넣기 핸들러 훅 ──────────────────────────────────────────────
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import { BULK_COLUMNS } from './constants';
import {
  createEmptyBulkRow, isBulkRowEmpty,
  normalizeBulkCellValue, parseClipboardMatrix,
} from './utils';

/**
 * 대량 붙여넣기 모달의 핸들러를 반환합니다.
 * @param {object} state   - useLeaveManagement 반환값
 * @param {object} derived - useLeaveDerived 반환값
 * @param {string} apiBase - API 베이스 경로
 */
export function useBulkHandlers(state, derived, apiBase) {
  const { showToast } = useToast();
  const { setBulkRows, setBulkOpen, setBulkSubmitting, userMap, fetchAll } = state;
  const { bulkRows, bulkErrors } = { ...state, ...derived };

  // ── 행 추가 ────────────────────────────────────────────────────────────────
  function addBulkRows(count = 10) {
    setBulkRows((prev) => [...prev, ...Array.from({ length: count }, createEmptyBulkRow)]);
  }

  // ── 빈 행 정리 ─────────────────────────────────────────────────────────────
  function trimEmptyBulkRows() {
    const filtered = bulkRows.filter((row) => !isBulkRowEmpty(row));
    setBulkRows(filtered.length > 0 ? filtered : [createEmptyBulkRow()]);
  }

  // ── 셀 값 변경 ─────────────────────────────────────────────────────────────
  function handleBulkCellChange(rowIndex, key, rawValue) {
    const normalized = normalizeBulkCellValue(key, rawValue);
    setBulkRows((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [key]: normalized };
      return next;
    });
  }

  // ── 셀 붙여넣기 (스프레드시트 범위 지원) ──────────────────────────────────
  function handleBulkCellPaste(event, startRowIndex, startColIndex) {
    const text = event.clipboardData.getData('text/plain');
    const matrix = parseClipboardMatrix(text);
    if (matrix.length === 0) return;
    if (matrix.length > 1 || (matrix[0] && matrix[0].length > 1)) event.preventDefault();
    setBulkRows((prev) => {
      const next = [...prev];
      const requiredRows = startRowIndex + matrix.length;
      while (next.length < requiredRows) next.push(createEmptyBulkRow());
      matrix.forEach((rowValues, rowOffset) => {
        rowValues.forEach((cellValue, colOffset) => {
          const col = BULK_COLUMNS[startColIndex + colOffset];
          if (!col) return;
          const targetRowIndex = startRowIndex + rowOffset;
          next[targetRowIndex] = {
            ...next[targetRowIndex],
            [col.key]: normalizeBulkCellValue(col.key, cellValue),
          };
        });
      });
      return next;
    });
  }

  // ── 대량 추가 제출 ─────────────────────────────────────────────────────────
  async function submitBulk() {
    const nonEmptyRows = bulkRows.filter((row) => !isBulkRowEmpty(row));
    if (nonEmptyRows.length === 0) { showToast('붙여넣은 데이터가 없습니다.', 'error'); return; }
    const hasInvalid = bulkRows.some((row, i) =>
      !isBulkRowEmpty(row) && BULK_COLUMNS.some((col) => Boolean(bulkErrors[i][col.key]))
    );
    if (hasInvalid) { showToast('빨간색 셀의 값을 수정한 뒤 다시 시도해주세요.', 'error'); return; }

    const payloadRows = nonEmptyRows.map((row) => ({
      user_id: Number(userMap.get(String(row.name || '').trim())),
      date:     String(row.date).trim(),
      type:     String(row.type).trim(),
      category: String(row.category).trim(),
      count:    Number(row.count),
      reason:   String(row.reason || '').trim() || null,
    }));

    setBulkSubmitting(true);
    const result = await apiClient.post(`${apiBase}/bulk`, { rows: payloadRows });
    setBulkSubmitting(false);
    if (!result.ok) { showToast(result.data?.message || '대량 추가에 실패했습니다.', 'error'); return; }
    showToast(`${payloadRows.length}건을 대량 추가했습니다.`, 'success');
    setBulkOpen(false);
    setBulkRows(Array.from({ length: 12 }, createEmptyBulkRow));
    fetchAll();
  }

  // ── 대량 모달 닫기 (상태 초기화) ──────────────────────────────────────────
  function closeBulkModal() {
    setBulkOpen(false);
    setBulkSubmitting(false);
    setBulkRows(Array.from({ length: 12 }, createEmptyBulkRow));
  }

  return {
    addBulkRows, trimEmptyBulkRows,
    handleBulkCellChange, handleBulkCellPaste,
    submitBulk, closeBulkModal,
  };
}
